/**
 * A custom element that renders a live countdown to the upcoming midnight
 * in the visitor's local timezone. Rolls over to the next day automatically.
 *
 * Expects child elements with [data-countdown-hours], [data-countdown-minutes]
 * and [data-countdown-seconds] attributes.
 */
class CountdownTimer extends HTMLElement {
  /** @type {number | undefined} */
  #interval;

  /** @type {number | undefined} */
  #renderedDay;

  connectedCallback() {
    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
  }

  #tick() {
    const now = new Date();
    const end = new Date(now);
    // Upcoming midnight in the visitor's local timezone.
    end.setHours(24, 0, 0, 0);

    const remaining = Math.max(0, end.getTime() - now.getTime());

    const hours = Math.floor(remaining / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    this.#set('hours', hours);
    this.#set('minutes', minutes);
    this.#set('seconds', seconds);

    // Refresh the displayed date on first render and when midnight rolls over.
    if (now.getDate() !== this.#renderedDay) {
      this.#renderedDay = now.getDate();
      this.#updateDate(now);
    }
  }

  /**
   * Renders the visitor's current date, e.g. "15TH JULY" (English) or
   * "15 juillet" (other locales, where English ordinals don't apply).
   * @param {Date} now
   */
  #updateDate(now) {
    const element = this.querySelector('[data-countdown-date]');
    if (!element) return;

    const language = document.documentElement.lang || 'en';
    const day = now.getDate();

    let month;
    try {
      month = now.toLocaleDateString(language, { month: 'long' });
    } catch {
      month = now.toLocaleDateString('en', { month: 'long' });
    }

    if (language.toLowerCase().startsWith('en')) {
      const suffixes = ['th', 'st', 'nd', 'rd'];
      const mod100 = day % 100;
      const suffix = suffixes[(mod100 - 20) % 10] || suffixes[mod100] || suffixes[0];
      element.textContent = `${day}${suffix} ${month}`;
    } else {
      element.textContent = `${day} ${month}`;
    }
  }

  /**
   * @param {string} unit
   * @param {number} value
   */
  #set(unit, value) {
    const element = this.querySelector(`[data-countdown-${unit}]`);
    if (element) element.textContent = String(value).padStart(2, '0');
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
